import { useState } from 'react';
import {
  Button,
  Breadcrumb,
  Typography,
  theme,
  Spin,
  App,
  Card,
  Collapse,
  Divider,
  Switch,
  Tag,
  Tabs,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useCategoryDetail, useCategoryMutations } from '@/hooks';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import VendorListPage from '@/components/vendors/VendorListPage';
import CustomerListPage from '@/components/customers/CustomerListPage';
import CategoryFormModal from './CategoryFormModal';
import DeactivateCategoryModal from './DeactivateCategoryModal';
import { getOrganizationMenuItems } from '@/constants/navigation.constants';
import { ConfirmationDialog, useConfirmationDialog } from '@/components/common/ConfirmationDialog';
import type { CategoryDetail, CategoryListItem } from '@/types/api/category';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import { TimestampPair } from '@/components/common/TimestampDisplay';
import PageContainer from '@/components/common/PageContainer';

const { Text } = Typography;

const CategoryDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = theme.useToken();
  const elevation = useElevation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { t } = useVendorTranslation();
  const categoryId = id ? parseInt(id) : null;

  // Fetch category details using hook
  const { category, isLoading, error, refresh, mutate } = useCategoryDetail(categoryId);
  const { toggleStatus } = useCategoryMutations();

  const [toggling, setToggling] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false);

  // Confirmation dialog for activate only (deactivate uses separate modal)
  const toggleStatusDialog = useConfirmationDialog<CategoryDetail>();

  // Handle status toggle
  const handleStatusToggle = () => {
    if (!category) return;

    if (category.isActive) {
      // Deactivating - use the new DeactivateCategoryModal
      setDeactivateModalVisible(true);
    } else {
      // Activating - use simple confirmation dialog
      toggleStatusDialog.open(category);
    }
  };

  // Design settings (matching VendorDetailPage)
  const currentColorScheme = {
    headerBg: token.colorBgContainer,
    headerAccent: token.colorPrimary,
    cardShadow: token.boxShadow,
    borderColor: token.colorBorderSecondary,
    highlightBg: token.colorPrimaryBg,
  };

  const currentFontSizes = {
    title: token.fontSizeHeading2,
    subtitle: token.fontSize,
    body: token.fontSize,
    label: token.fontSizeSM,
    menuItem: token.fontSizeLG,
  };



  if (isLoading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !category) {
    return (
      <PageContainer>
        <Typography.Text type="danger">
          {error ? `Error: ${error.message}` : t('vendor.messages.categoryNotFound')}
        </Typography.Text>
        <div style={{ marginTop: token.marginLG }}>
          <Button onClick={() => navigate('/auth/categories')}>
            {t('vendor.buttons.backToCategories')}
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Merged Breadcrumb + Header Card */}
      <Card
        className="tw-mb-6"
        style={{
          ...elevation.card,
          background: currentColorScheme.headerBg,
        }}
      >
        {/* Back button + Breadcrumb aligned horizontally */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/auth/categories')}
            style={{
              fontSize: currentFontSizes.body,
              padding: 0,
              height: 'auto',
              lineHeight: 1,
            }}
          >
            {t('vendor.buttons.back')}
          </Button>
          <Breadcrumb
            items={[
              {
                title: t('vendor.breadcrumbs.masterData'),
              },
              {
                title: <a onClick={() => navigate('/auth/categories')}>{t('vendor.breadcrumbs.organization')}</a>,
                menu: { items: getOrganizationMenuItems(navigate, t)?.filter((item): item is NonNullable<typeof item> => item !== null) },
              },
              {
                title: t('vendor.breadcrumbs.category'),
              },
              {
                title: category.category,
              },
            ]}
          />
        </div>

        <Divider style={{ margin: `${token.marginSM}px 0` }} />

        {/* Header content — primary identity (name) + primary action */}
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          <div className="tw-flex tw-items-center tw-gap-4 tw-flex-1">
            <div className="tw-flex-1">
              <h1
                style={{
                  fontSize: currentFontSizes.title,
                  fontWeight: token.fontWeightStrong,
                  lineHeight: 1,
                  color: currentColorScheme.headerAccent,
                  margin: 0,
                }}
              >
                {category.category}
              </h1>
            </div>
          </div>

          <Tooltip title={category.isActive ? undefined : t('vendor.actions.reactivateToEdit')}>
            <span
              style={{
                display: 'inline-block',
                cursor: category.isActive ? undefined : 'not-allowed',
              }}
            >
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setEditModalVisible(true)}
                disabled={!category.isActive}
                style={{
                  height: `${controlHeightTokens.controlHeightXL}px`,
                  fontSize: currentFontSizes.body,
                  ...(category.isActive ? {} : { pointerEvents: 'none' }),
                }}
              >
                {t('vendor.actions.edit')}
              </Button>
            </span>
          </Tooltip>
        </div>

        <Divider style={{ margin: `${token.margin}px 0` }} />

        <div className="tw-flex tw-gap-8 tw-flex-wrap" style={{ paddingTop: token.paddingXS }}>
          {/* Type - only show if typeInfo exists */}
          {category.typeInfo && category.typeInfo.length > 0 && (
            <div className="tw-flex tw-items-center tw-gap-2">
              <FileTextOutlined
                style={{ fontSize: currentFontSizes.body, color: token.colorTextSecondary }}
              />
              <span
                style={{
                  fontSize: currentFontSizes.label,
                  color: token.colorTextSecondary,
                }}
              >
                {t('vendor.fields.categoryType')}:
              </span>
              <div className="tw-flex tw-gap-1 tw-flex-wrap">
                {category.typeInfo.map((type) => (
                  <Tag
                    key={type.typeId}
                    bordered={false}
                    color="purple"
                    style={{
                      borderRadius: radiusTokens.control,
                    }}
                  >
                    {type.type}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {/* Color */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <span
              style={{
                fontSize: currentFontSizes.label,
                color: token.colorTextSecondary,
              }}
            >
              {t('vendor.fields.color')}:
            </span>
            <div
              style={{
                width: token.sizeMD,
                height: token.sizeMD,
                borderRadius: radiusTokens.chip,
                backgroundColor: category.color,
                border: `${token.lineWidth}px solid ${token.colorBorder}`,
              }}
            />
            <Text style={{ fontFamily: 'monospace', fontSize: currentFontSizes.label }}>
              {category.color}
            </Text>
          </div>

          {/* Status with Switch */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <CheckCircleOutlined
              style={{ fontSize: currentFontSizes.body, color: token.colorTextSecondary }}
            />
            <span
              style={{
                fontSize: currentFontSizes.label,
                color: token.colorTextSecondary,
              }}
            >
              {t('vendor.fields.status')}:
            </span>
            <span style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
              {category.isActive ? t('vendor.status.active') : t('vendor.status.inactive')}
            </span>
            <Switch
              checked={category.isActive}
              onChange={handleStatusToggle}
              loading={toggling}
              style={{
                transform: 'scale(0.8)',
              }}
            />
          </div>
        </div>
      </Card>

      {/* Secondary details behind a disclosure — notes + timestamps (progressive disclosure) */}
      <Card
        className="tw-mb-6"
        style={{
          ...elevation.card,
        }}
        styles={{ body: { padding: token.paddingSM } }}
      >
        <Collapse
          ghost
          items={[
            {
              key: 'additional',
              label: <Text strong>{t('vendor.sections.additional')}</Text>,
              children: (
                <div className="tw-flex tw-flex-col tw-gap-4">
                  {/* Notes */}
                  <div>
                    <div
                      style={{
                        fontSize: currentFontSizes.label,
                        color: token.colorTextSecondary,
                        marginBottom: token.marginXXS,
                      }}
                    >
                      {t('vendor.sections.notes')}
                    </div>
                    <Text>{category.description || t('vendor.labels.noDescription')}</Text>
                  </div>

                  {/* Timestamps */}
                  <TimestampPair
                    createdAt={category.createdAt}
                    updatedAt={category.updatedAt}
                    direction="horizontal"
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Vendors/Customers in this Category — clearly-secondary association card */}
      <Card
        style={{
          ...elevation.card,
        }}
      >
        <Tabs
          defaultActiveKey="vendor"
          items={[
            {
              key: 'vendor',
              label: t('vendor.tabs.vendor'),
              children: (
                <VendorListPage
                  compact={true}
                  initialCategories={categoryId ? [categoryId] : []}
                  showCategoryColumn={false}
                  defaultActiveOnly={true}
                />
              ),
            },
            {
              key: 'customer',
              label: t('vendor.tabs.customer'),
              children: (
                <CustomerListPage
                  compact={true}
                  initialCategories={categoryId ? [categoryId] : []}
                  showCategoryColumn={false}
                  defaultActiveOnly={true}
                />
              ),
            },
          ]}
        />
      </Card>

      {/* Edit Category Modal */}
      <CategoryFormModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        onSuccess={(updatedCategory) => {
          if (updatedCategory) {
            // Immediately update local state with new data for instant UI refresh
            mutate({
              ...category,
              category: updatedCategory.category,
              color: updatedCategory.color,
              description: updatedCategory.description,
              typeInfo: updatedCategory.typeInfo,
              isActive: updatedCategory.isActive,
              updatedAt: updatedCategory.updatedAt,
            });
          }
          // Also refresh to ensure cache consistency
          refresh();
        }}
        category={
          category
            ? {
                id: category.categoryId.toString(),
                name: category.category,
                type: (category.typeInfo && category.typeInfo.length > 0
                  ? (category.typeInfo[0].type.toLowerCase() as 'vendor' | 'customer')
                  : 'vendor'),
                color: category.color,
                notes: category.description || '',
                status: category.isActive ? 'active' : 'inactive',
                vendorCount: 0,
                createdAt: '',
                updatedAt: '',
              }
            : undefined
        }
      />

      {/* Confirmation Dialog for Activate Only */}
      <ConfirmationDialog
        open={toggleStatusDialog.isOpen}
        onClose={toggleStatusDialog.close}
        onConfirm={async () => {
          const categoryData = toggleStatusDialog.data;
          if (categoryData && categoryId) {
            setToggling(true);
            try {
              await toggleStatus(categoryId, true);
              message.success(t('vendor.messages.categoryActionSuccess', { action: t('vendor.status.active').toLowerCase() }));
              await refresh();
              toggleStatusDialog.close();
            } catch (error) {
              message.error(t('vendor.messages.statusUpdateFailed'));
              console.error('Error activating category:', error);
            } finally {
              setToggling(false);
            }
          }
        }}
        title={t('vendor.notifications.activateCategory')}
        message={t('vendor.messages.confirmStatusChange', {
          action: t('vendor.actions.activate').toLowerCase(),
          name: toggleStatusDialog.data?.category || '',
        })}
        type="info"
        confirmText={t('vendor.actions.activate')}
        loading={toggling}
      />

      {/* Deactivate Category Modal with Batch Replace */}
      <DeactivateCategoryModal
        visible={deactivateModalVisible}
        onClose={() => setDeactivateModalVisible(false)}
        onSuccess={() => {
          refresh();
        }}
        category={category ? {
          categoryId: category.categoryId,
          category: category.category,
          typeInfo: category.typeInfo || [],
          description: category.description || '',
          color: category.color || '',
          isActive: category.isActive,
        } as CategoryListItem : null}
      />
    </PageContainer>
  );
};

export default CategoryDetailPage;
